import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { BundleOffering } from "@/pages/bundles/types/grouped-bundle";
import { useChangeBundlePricesBulk } from "../hooks/use-change-bundle-price";
import { usePartialReleaseBundlesBulk } from "../hooks/use-pricing-lifecycle";

type Mode = "INCREASE" | "DECREASE";
type ValueType = "AMOUNT" | "PERCENT";
export type BulkMode = "change" | "rollback";
type ApplyMode = "NOW" | "PARTIAL";

function toLocalInput(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

// Earliest a shared release may be scheduled: now, or 1 min after the latest
// existing release across the offerings (releases must be strictly increasing).
function earliestRelease(latestReleaseAt?: string | null): Date {
  const now = new Date();
  now.setSeconds(0, 0);
  if (latestReleaseAt) {
    const afterLatest = new Date(new Date(latestReleaseAt).getTime() + 60_000);
    if (afterLatest > now) return afterLatest;
  }
  return now;
}

function computed(current: number, mode: Mode, valueType: ValueType, value: number) {
  if (Number.isNaN(value)) return current;
  const sign = mode === "DECREASE" ? -1 : 1;
  if (valueType === "PERCENT") return Math.round(current * (1 + (sign * value) / 100));
  return Math.round(current + sign * value);
}

export default function BulkPriceChangeDialog({
  open,
  variant,
  productName,
  offerings,
  onClose,
}: {
  open: boolean;
  variant: BulkMode;
  productName: string;
  offerings: BundleOffering[];
  onClose: () => void;
}) {
  const bulk = useChangeBundlePricesBulk();
  const partialBulk = usePartialReleaseBundlesBulk();
  const isRollback = variant === "rollback";
  // Apply mode — only for the "change" variant (rollback is always for everyone).
  const [applyMode, setApplyMode] = useState<ApplyMode>("NOW");
  // A shared partial release must be after EVERY offering's latest existing
  // release, so floor the picker to the max of them.
  const maxLatestRelease = offerings.reduce<string | null>((max, o) => {
    const exps = o.experimental ?? [];
    const latest = exps[exps.length - 1]?.released_at ?? null;
    if (latest && (!max || new Date(latest) > new Date(max))) return latest;
    return max;
  }, null);
  const minReleaseLocal = toLocalInput(earliestRelease(maxLatestRelease));
  const [releasedAt, setReleasedAt] = useState<string>(minReleaseLocal);
  const isPartial = applyMode === "PARTIAL" && !isRollback;

  // change → every offering with a current price; rollback → only those that
  // also have a previous price to revert to (history[0] = most recent past).
  const rows = useMemo(
    () =>
      offerings
        .filter((o) => o.current && (!isRollback || o.history.length > 0))
        .map((o) => ({
          bundleId: o.current!.id,
          name: o.name,
          current: o.current!.amount,
          previous: o.history[0]?.amount ?? null,
          currency: o.current!.currency,
          sessions: o.current!.session_quantity ?? o.session_quantity ?? 0,
        })),
    [offerings, isRollback],
  );

  const [mode, setMode] = useState<Mode>("INCREASE");
  const [valueType, setValueType] = useState<ValueType>("PERCENT");
  const [value, setValue] = useState<string>("");
  // Per-row final amount (editable). Defaults: change → current; rollback → previous.
  const [expected, setExpected] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      rows.map((r) => [
        r.bundleId,
        String(isRollback ? (r.previous ?? r.current) : r.current),
      ]),
    ),
  );

  const applyToAll = () => {
    const v = parseFloat(value);
    if (Number.isNaN(v)) return;
    setExpected(
      Object.fromEntries(
        rows.map((r) => [
          r.bundleId,
          String(computed(r.current, mode, valueType, v)),
        ]),
      ),
    );
  };

  const resetToPrevious = () =>
    setExpected(
      Object.fromEntries(
        rows.map((r) => [r.bundleId, String(r.previous ?? r.current)]),
      ),
    );

  const fmt = (n: number, currency: string) =>
    `${currency} ${n.toLocaleString()}`;
  const perSession = (amount: number, sessions: number, currency: string) =>
    sessions > 0 ? `${fmt(Math.round(amount / sessions), currency)}/session` : "—";

  const changes = rows
    .map((r) => ({ id: r.bundleId, amount: Math.round(parseFloat(expected[r.bundleId])) }))
    .filter((c) => !Number.isNaN(c.amount) && c.amount >= 0);
  const effective = changes.filter((c) => {
    const row = rows.find((r) => r.bundleId === c.id);
    return row && c.amount !== row.current;
  });

  const busy = bulk.isPending || partialBulk.isPending;

  const submit = () => {
    if (!effective.length) {
      toast({ title: "Nothing to change", description: "No prices were modified." });
      return;
    }

    if (isPartial) {
      partialBulk.mutate(
        {
          releases: effective.map((c) => ({ baselineId: c.id, amount: c.amount })),
          released_at: new Date(releasedAt).toISOString(),
        },
        {
          onSuccess: () => {
            toast({
              title: "Partial releases created",
              description: `${effective.length} experimental price(s) released to new cohorts. Existing users keep theirs.`,
            });
            onClose();
          },
          onError: (e: any) =>
            toast({
              title: "Error",
              description:
                e.response?.data?.message || "Failed to create partial releases",
              variant: "destructive",
            }),
        },
      );
      return;
    }

    bulk.mutate(effective, {
      onSuccess: () => {
        toast({
          title: isRollback ? "Prices rolled back" : "Prices updated",
          description: `${effective.length} price(s) changed. Old prices moved to history.`,
        });
        onClose();
      },
      onError: (e: any) =>
        toast({
          title: "Error",
          description: e.response?.data?.message || "Failed to change prices",
          variant: "destructive",
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isRollback ? "Roll back all prices" : "Change all prices"} —{" "}
            {productName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isRollback ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Each price is set to its previous value — edit any below.
              </p>
              <Button variant="outline" size="sm" onClick={resetToPrevious}>
                Reset to previous
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Change</p>
                <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INCREASE">Increase by</SelectItem>
                    <SelectItem value="DECREASE">Decrease by</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <p className="text-xs text-gray-500 mb-1">By</p>
                <Select
                  value={valueType}
                  onValueChange={(v) => setValueType(v as ValueType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENT">Percent</SelectItem>
                    <SelectItem value="AMOUNT">Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24">
                <p className="text-xs text-gray-500 mb-1">Value</p>
                <Input
                  type="number"
                  min={0}
                  placeholder="e.g. 10"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>
              <Button variant="outline" onClick={applyToAll}>
                Apply
              </Button>
            </div>
          )}

          <div className="max-h-[50vh] overflow-y-auto rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-2 px-3">Offering</th>
                  <th className="py-2 px-3">Current</th>
                  {isRollback && <th className="py-2 px-3">Previous</th>}
                  <th className="py-2 px-3 w-44">Expected (editable)</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isRollback ? 4 : 3}
                      className="py-4 px-3 text-center text-gray-400"
                    >
                      {isRollback
                        ? "No offerings with a previous price."
                        : "No active prices to change."}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const exp = Math.round(parseFloat(expected[r.bundleId]));
                    return (
                      <tr key={r.bundleId} className="border-t border-gray-100">
                        <td className="py-2 px-3 align-top">
                          {r.name}
                          {r.sessions > 0 && (
                            <span className="block text-[11px] text-gray-400">
                              {r.sessions} session(s)
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 align-top text-gray-500">
                          {fmt(r.current, r.currency)}
                          <span className="block text-[11px] text-gray-400">
                            {perSession(r.current, r.sessions, r.currency)}
                          </span>
                        </td>
                        {isRollback && (
                          <td className="py-2 px-3 align-top text-gray-500">
                            {r.previous !== null
                              ? fmt(r.previous, r.currency)
                              : "—"}
                          </td>
                        )}
                        <td className="py-2 px-3 align-top">
                          <Input
                            type="number"
                            min={0}
                            className="h-8"
                            value={expected[r.bundleId] ?? String(r.current)}
                            onChange={(e) =>
                              setExpected((prev) => ({
                                ...prev,
                                [r.bundleId]: e.target.value,
                              }))
                            }
                          />
                          <span className="block text-[11px] text-gray-400 mt-1">
                            {Number.isNaN(exp)
                              ? "—"
                              : perSession(exp, r.sessions, r.currency)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!isRollback && (
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Apply</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={applyMode === "NOW" ? "default" : "outline"}
                    onClick={() => setApplyMode("NOW")}
                  >
                    Now — everyone
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={applyMode === "PARTIAL" ? "default" : "outline"}
                    onClick={() => setApplyMode("PARTIAL")}
                  >
                    Partial release
                  </Button>
                </div>
              </div>
              {isPartial && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Release date &amp; time</p>
                  <Input
                    type="datetime-local"
                    className="h-9"
                    min={minReleaseLocal}
                    value={releasedAt}
                    onChange={(e) => setReleasedAt(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-gray-500">
              {effective.length} of {rows.length}{" "}
              {isPartial ? "to release" : "will change"}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                disabled={
                  !effective.length ||
                  busy ||
                  (isPartial && (!releasedAt || releasedAt < minReleaseLocal))
                }
                onClick={submit}
              >
                {busy
                  ? "Saving…"
                  : isPartial
                    ? "Release"
                    : isRollback
                      ? "Roll back"
                      : "Apply changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
